<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { Line, Bar, Pie, Doughnut } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, PointElement, LineElement, ArcElement } from 'chart.js';

// Chart.js registrieren
ChartJS.register(
  Title, Tooltip, Legend, BarElement, CategoryScale, 
  LinearScale, PointElement, LineElement, ArcElement
);

// Daten und Zustände
const isLoading = ref(true);
const error = ref(null);
const activeTab = ref('overview');
const timeRange = ref('30'); // Standard: 30 Tage
const forceRender = ref(0); // Hilft beim Neurendern der Diagramme
const isResetting = ref(false); // Status für das Zurücksetzen der Statistiken
const resetSuccess = ref(null); // Erfolgsmeldung für das Zurücksetzen
const resetError = ref(null); // Fehlermeldung für das Zurücksetzen

// Statistikdaten
const summaryData = ref(null);
const dailyData = ref(null);
const browserData = ref(null);
const osData = ref(null);
const deviceData = ref(null);
const pageData = ref(null);
const countryData = ref(null);

// Dark Mode Status erkennen
const isDarkMode = computed(() => {
  return document.body.classList.contains('dark-mode');
});

// Farbpaletten basierend auf Dark Mode
const chartColors = computed(() => {
  return isDarkMode.value ? {
    primary: '#4db380',
    secondary: '#64b5f6',
    accent: '#ffca28',
    purple: '#ce93d8',
    red: '#ef5350',
    blue: '#5c6bc0',
    green: '#66bb6a',
    brown: '#a1887f',
    orange: '#ff7043',
    grey: '#78909c',
    lightGrey: '#bdbdbd',
    text: '#e9ecef',
    line: '#343a40',
    grid: 'rgba(255, 255, 255, 0.1)',
    background: '#2a2e33',
    backgroundTransparent: 'rgba(42, 46, 51, 0.7)'
  } : {
    primary: '#00843D',
    secondary: '#2196F3',
    accent: '#FFC107',
    purple: '#9C27B0',
    red: '#F44336',
    blue: '#3F51B5',
    green: '#4CAF50',
    brown: '#795548',
    orange: '#FF5722',
    grey: '#607D8B',
    lightGrey: '#9E9E9E',
    text: '#333333',
    line: '#eaeaea',
    grid: 'rgba(0, 0, 0, 0.1)',
    background: '#ffffff',
    backgroundTransparent: 'rgba(255, 255, 255, 0.7)'
  };
});

// Diagramm-Optionen dynamisch basierend auf Dark Mode
const chartOptions = computed(() => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: chartColors.value.text,
          padding: 15,
          font: {
            size: 12
          },
          usePointStyle: true
        }
      },
      title: {
        display: true,
        text: 'Besucherstatistik',
        color: chartColors.value.text,
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: chartColors.value.backgroundTransparent,
        titleColor: chartColors.value.text,
        bodyColor: chartColors.value.text,
        borderColor: chartColors.value.line,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 6,
        displayColors: true,
        usePointStyle: true,
        boxPadding: 6
      }
    },
    scales: {
      x: {
        grid: {
          color: chartColors.value.grid
        },
        ticks: {
          color: chartColors.value.text,
          font: {
            size: 11
          }
        },
        border: {
          color: chartColors.value.grid
        }
      },
      y: {
        grid: {
          color: chartColors.value.grid
        },
        ticks: {
          color: chartColors.value.text,
          font: {
            size: 11
          }
        },
        border: {
          color: chartColors.value.grid
        }
      }
    }
  };
});

// Chart-Daten berechnen
const dailyChartData = computed(() => {
  if (!dailyData.value) return null;
  
  return {
    labels: dailyData.value.map(item => item.date),
    datasets: [
      {
        label: 'Seitenaufrufe',
        data: dailyData.value.map(item => item.visits),
        borderColor: chartColors.value.primary,
        backgroundColor: isDarkMode.value 
          ? 'rgba(77, 179, 128, 0.2)'
          : 'rgba(0, 132, 61, 0.2)',
        borderWidth: 2,
        tension: 0.4
      },
      {
        label: 'Eindeutige Besucher',
        data: dailyData.value.map(item => item.unique_visitors),
        borderColor: chartColors.value.secondary,
        backgroundColor: isDarkMode.value
          ? 'rgba(100, 181, 246, 0.2)'
          : 'rgba(33, 150, 243, 0.2)',
        borderWidth: 2,
        tension: 0.4
      }
    ]
  };
});

const browserChartData = computed(() => {
  if (!browserData.value) return null;
  
  return {
    labels: browserData.value.map(item => item.browser),
    datasets: [{
      data: browserData.value.map(item => item.count),
      backgroundColor: [
        chartColors.value.primary,
        chartColors.value.secondary, 
        chartColors.value.accent, 
        chartColors.value.purple, 
        chartColors.value.red, 
        chartColors.value.blue, 
        chartColors.value.green, 
        chartColors.value.brown
      ]
    }]
  };
});

const osChartData = computed(() => {
  if (!osData.value) return null;
  
  return {
    labels: osData.value.map(item => item.os),
    datasets: [{
      data: osData.value.map(item => item.count),
      backgroundColor: [
        chartColors.value.primary,
        chartColors.value.secondary, 
        chartColors.value.accent, 
        chartColors.value.purple, 
        chartColors.value.red, 
        chartColors.value.blue, 
        chartColors.value.green, 
        chartColors.value.brown
      ]
    }]
  };
});

const deviceChartData = computed(() => {
  if (!deviceData.value) return null;
  
  return {
    labels: deviceData.value.map(item => item.device_type),
    datasets: [{
      data: deviceData.value.map(item => item.count),
      backgroundColor: [
        chartColors.value.primary,
        chartColors.value.secondary, 
        chartColors.value.accent
      ]
    }]
  };
});

const countryChartData = computed(() => {
  if (!countryData.value) return null;
  
  // Nehme nur die Top 10 Länder und fasse den Rest unter "Andere" zusammen
  let topCountries = [...countryData.value].slice(0, 10);
  let otherCount = 0;
  
  if (countryData.value.length > 10) {
    for (let i = 10; i < countryData.value.length; i++) {
      otherCount += parseInt(countryData.value[i].count);
    }
    
    if (otherCount > 0) {
      topCountries.push({ country: 'Andere', count: otherCount });
    }
  }
  
  return {
    labels: topCountries.map(item => item.country),
    datasets: [{
      data: topCountries.map(item => item.count),
      backgroundColor: [
        chartColors.value.primary,
        chartColors.value.secondary, 
        chartColors.value.accent, 
        chartColors.value.purple, 
        chartColors.value.red, 
        chartColors.value.blue, 
        chartColors.value.green, 
        chartColors.value.brown,
        chartColors.value.orange, 
        chartColors.value.grey, 
        chartColors.value.lightGrey
      ]
    }]
  };
});

// Daten laden
const loadStats = async (type) => {
  isLoading.value = true;
  error.value = null;
  
  try {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      throw new Error('Nicht authentifiziert. Bitte melden Sie sich an.');
    }
    
    const response = await fetch(`/api/stats.php?type=${type}&days=${timeRange.value}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Fehler beim Laden der Statistiken: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Je nach Typ die entsprechenden Daten setzen
    switch (type) {
      case 'summary':
        summaryData.value = data;
        break;
      case 'daily':
        dailyData.value = data;
        break;
      case 'browsers':
        browserData.value = data;
        break;
      case 'os':
        osData.value = data;
        break;
      case 'devices':
        deviceData.value = data;
        break;
      case 'pages':
        pageData.value = data;
        break;
      case 'countries':
        countryData.value = data;
        break;
    }
    
  } catch (err) {
    console.error('Fehler beim Laden der Statistiken:', err);
    error.value = err.message;
    
    // Fallback für Entwicklung (Beispieldaten)
    if (process.env.NODE_ENV === 'development') {
      if (type === 'summary') {
        summaryData.value = {
          total_visits: 1568,
          unique_visitors: 327,
          mobile_count: 892,
          desktop_count: 676,
          top_pages: [
            { page: '/home', count: 456 },
            { page: '/about', count: 234 },
            { page: '/music', count: 189 },
            { page: '/shows', count: 145 },
            { page: '/gallery', count: 98 }
          ]
        };
      } else if (type === 'daily') {
        // Letzten 30 Tage generieren
        const days = [];
        for (let i = 0; i < parseInt(timeRange.value); i++) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const formattedDate = date.toISOString().split('T')[0];
          days.unshift({
            date: formattedDate,
            visits: Math.floor(Math.random() * 80) + 20,
            unique_visitors: Math.floor(Math.random() * 20) + 5
          });
        }
        dailyData.value = days;
      } else if (type === 'browsers') {
        browserData.value = [
          { browser: 'Chrome', count: 786 },
          { browser: 'Safari', count: 382 },
          { browser: 'Firefox', count: 215 },
          { browser: 'Edge', count: 142 },
          { browser: 'Opera', count: 29 },
          { browser: 'Unbekannt', count: 14 }
        ];
      } else if (type === 'os') {
        osData.value = [
          { os: 'Windows', count: 523 },
          { os: 'macOS', count: 412 },
          { os: 'iOS', count: 314 },
          { os: 'Android', count: 287 },
          { os: 'Linux', count: 32 }
        ];
      } else if (type === 'devices') {
        deviceData.value = [
          { device_type: 'Desktop', count: 676 },
          { device_type: 'Mobile', count: 812 },
          { device_type: 'Tablet', count: 80 }
        ];
      } else if (type === 'pages') {
        pageData.value = [
          { page: '/home', count: 456 },
          { page: '/about', count: 234 },
          { page: '/music', count: 189 },
          { page: '/shows', count: 145 },
          { page: '/gallery', count: 98 }
        ];
      } else if (type === 'countries') {
        countryData.value = [
          { country: 'Deutschland', count: 827 },
          { country: 'Österreich', count: 234 },
          { country: 'Schweiz', count: 156 },
          { country: 'USA', count: 98 },
          { country: 'Großbritannien', count: 76 },
          { country: 'Irland', count: 52 },
          { country: 'Frankreich', count: 43 },
          { country: 'Niederlande', count: 35 },
          { country: 'Italien', count: 27 },
          { country: 'Belgien', count: 20 }
        ];
      }
    }
  } finally {
    isLoading.value = false;
  }
};

// Alle verfügbaren Statistiken laden
const loadAllStats = async () => {
  await Promise.all([
    loadStats('summary'),
    loadStats('daily'),
    loadStats('browsers'),
    loadStats('os'),
    loadStats('devices'),
    loadStats('pages'),
    loadStats('countries')
  ]);
};

// Beim Tab-Wechsel die entsprechenden Daten laden
watch(activeTab, (newTab) => {
  if (newTab === 'overview' && !summaryData.value) {
    loadStats('summary');
  } else if (newTab === 'browsers' && !browserData.value) {
    loadStats('browsers');
  } else if (newTab === 'os' && !osData.value) {
    loadStats('os');
  } else if (newTab === 'devices' && !deviceData.value) {
    loadStats('devices');
  } else if (newTab === 'pages' && !pageData.value) {
    loadStats('pages');
  } else if (newTab === 'daily' && !dailyData.value) {
    loadStats('daily');
  } else if (newTab === 'countries' && !countryData.value) {
    loadStats('countries');
  }
});

// Bei Zeitraum-Änderung die täglichen Daten neu laden
watch(timeRange, (newRange) => {
  if (activeTab.value === 'daily') {
    loadStats('daily');
  }
});

// Dark Mode-Änderung überwachen und Charts aktualisieren
const initDarkModeWatch = () => {
  // MutationObserver erstellen, um Klassenänderungen am Body zu beobachten
  const observer = new MutationObserver(() => {
    // Wir aktualisieren nur die Chart-Darstellung, ohne die Daten neu zu laden
    // Das verbessert die Performanz und das Benutzererlebnis
    
    // forceRender erhöhen, um die Diagramme zu aktualisieren
    forceRender.value++;
    console.log('Dark Mode geändert, aktualisiere Charts');
  });

  // Observer starten
  observer.observe(document.body, { 
    attributes: true, 
    attributeFilter: ['class'] 
  });
};

// Initialisieren
onMounted(() => {
  loadAllStats();
  initDarkModeWatch();
});

// Statistiken zurücksetzen
const resetStatistics = async () => {
  if (!confirm('Möchten Sie wirklich alle Statistiken zurücksetzen? Dieser Vorgang kann nicht rückgängig gemacht werden.')) {
    return;
  }
  
  console.log('Statistik-Reset-Vorgang gestartet...');
  isResetting.value = true;
  resetError.value = null;
  resetSuccess.value = null;
  
  try {
    // Admin-Token aus localStorage holen, wie es auch in check_admin.php verwendet wird
    const adminToken = localStorage.getItem('adminToken');
    console.log('Admin-Token vorhanden:', !!adminToken);
    
    if (!adminToken) {
      resetError.value = 'Kein Admin-Token gefunden. Bitte melden Sie sich erneut an.';
      console.error('Kein Admin-Token gefunden');
      return;
    }
    
    console.log('Sende API-Anfrage zum Zurücksetzen der Statistiken...');
    
    const response = await fetch('/api/reset_statistics.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${adminToken}`
      },
      credentials: 'include' // Wichtig für Cookies
    });
    
    console.log('Antwort vom Server erhalten:', {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries([...response.headers])
    });
    
    // Prüfen auf Authentifizierungsfehler
    if (response.status === 401) {
      console.error('Authentifizierungsfehler: Nicht autorisiert');
      resetError.value = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
      return;
    }
    
    // Prüfen auf Server-Fehler
    if (!response.ok) {
      console.error(`Server-Fehler: ${response.status} ${response.statusText}`);
      resetError.value = `Server-Fehler: ${response.status} ${response.statusText}`;
      return;
    }
    
    // Sicheres Parsen von JSON mit Try/Catch
    let data;
    try {
      const text = await response.text();
      console.log('Rohantwort vom Server:', text);
      data = JSON.parse(text);
    } catch (parseError) {
      console.error('Fehler beim Parsen der JSON-Antwort:', parseError);
      resetError.value = 'Ungültiges Antwortformat vom Server';
      return;
    }
    
    console.log('API-Antwort-Daten:', data);
    
    if (data.success) {
      console.log('Statistiken erfolgreich zurückgesetzt!');
      resetSuccess.value = data.message;
      // Nach Erfolg die Daten neu laden
      loadAllStats();
    } else {
      console.error('Fehler beim Zurücksetzen der Statistiken:', data.message);
      resetError.value = data.message || 'Beim Zurücksetzen der Statistiken ist ein Fehler aufgetreten.';
    }
  } catch (error) {
    console.error('Exception beim Zurücksetzen der Statistiken:', error);
    resetError.value = `Beim Zurücksetzen der Statistiken ist ein Fehler aufgetreten: ${error.message}`;
  } finally {
    isResetting.value = false;
    console.log('Statistik-Reset-Vorgang abgeschlossen.');
    
    // Nach 5 Sekunden die Meldungen ausblenden
    setTimeout(() => {
      resetSuccess.value = null;
      resetError.value = null;
    }, 5000);
  }
};
</script>

<template>
  <div class="stats-manager">
    <div class="section-header">
      <h2>Statistiken</h2>
      <p class="section-description">
        Überwachen Sie die Besucherstatistik Ihrer Website
      </p>
    </div>
    
    <div class="admin-tabs">
      <button 
        @click="activeTab = 'overview'"
        :class="['tab-button', { active: activeTab === 'overview' }]"
      >
        Übersicht
      </button>
      <button 
        @click="activeTab = 'daily'"
        :class="['tab-button', { active: activeTab === 'daily' }]"
      >
        Zeitverlauf
      </button>
      <button 
        @click="activeTab = 'browsers'"
        :class="['tab-button', { active: activeTab === 'browsers' }]"
      >
        Browser
      </button>
      <button 
        @click="activeTab = 'os'"
        :class="['tab-button', { active: activeTab === 'os' }]"
      >
        Betriebssysteme
      </button>
      <button 
        @click="activeTab = 'devices'"
        :class="['tab-button', { active: activeTab === 'devices' }]"
      >
        Geräte
      </button>
      <button 
        @click="activeTab = 'pages'"
        :class="['tab-button', { active: activeTab === 'pages' }]"
      >
        Seiten
      </button>
      <button 
        @click="activeTab = 'countries'"
        :class="['tab-button', { active: activeTab === 'countries' }]"
      >
        Länder
      </button>
    </div>
    
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>Lade Statistiken...</p>
    </div>
    
    <div v-else-if="error" class="error-message">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <p>{{ error }}</p>
    </div>
    
    <div v-else class="stats-content">
      <!-- Übersichts-Tab -->
      <div v-if="activeTab === 'overview' && summaryData" class="overview-tab">
        <div class="card-container">
          <!-- Reset-Button und Meldungen -->
          <div class="stats-actions">
            <button 
              @click="resetStatistics" 
              class="reset-button" 
              :disabled="isResetting"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 2v6h6"></path>
                <path d="M3 13a9 9 0 1 0 3-7.7L3 8"></path>
              </svg>
              {{ isResetting ? 'Wird zurückgesetzt...' : 'Statistiken zurücksetzen' }}
            </button>
            
            <!-- Meldungen -->
            <div v-if="resetSuccess" class="reset-success">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              {{ resetSuccess }}
            </div>
            
            <div v-if="resetError" class="reset-error">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              {{ resetError }}
            </div>
          </div>
          
          <div class="stats-card today-card">
            <div class="card-header">
              <div class="card-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
              </div>
              <div class="card-title">Besuche heute</div>
            </div>
            <div class="card-body">
              <div class="stat-value">{{ summaryData.today_visits }}</div>
              <div class="stat-label">Besuche</div>
            </div>
          </div>
          
          <div class="stats-card yesterday-card">
            <div class="card-header">
              <div class="card-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
              </div>
              <div class="card-title">Besuche gestern</div>
            </div>
            <div class="card-body">
              <div class="stat-value">{{ summaryData.yesterday_visits || 0 }}</div>
              <div class="stat-label">Besuche</div>
            </div>
          </div>
          
          <div class="stats-card week-card">
            <div class="card-header">
              <div class="card-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
              </div>
              <div class="card-title">Letzte 7 Tage</div>
            </div>
            <div class="card-body">
              <div class="stat-value">{{ summaryData.last_week_visits || 0 }}</div>
              <div class="stat-label">Besuche</div>
            </div>
          </div>
          
          <div class="stats-cards">
            <div class="stat-card total-visits">
              <div class="stat-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M2 12h3a3 3 0 0 0 2.83-2h0a3 3 0 0 1 2.83-2h2.67a3 3 0 0 1 2.83 2h0a3 3 0 0 0 2.83 2H21"></path>
                  <path d="M2 16h3a3 3 0 0 0 2.83-2h0a3 3 0 0 1 2.83-2h2.67a3 3 0 0 1 2.83 2h0a3 3 0 0 0 2.83 2H21"></path>
                  <path d="M2 8h3a3 3 0 0 0 2.83-2h0a3 3 0 0 1 2.83-2h2.67a3 3 0 0 1 2.83 2h0a3 3 0 0 0 2.83 2H21"></path>
                </svg>
              </div>
              <div class="stat-info">
                <h3>Gesamtaufrufe</h3>
                <p class="stat-value">{{ summaryData.total_visits }}</p>
              </div>
            </div>
            
            <div class="stat-card unique-visitors">
              <div class="stat-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div class="stat-info">
                <h3>Eindeutige Besucher</h3>
                <p class="stat-value">{{ summaryData.unique_visitors }}</p>
              </div>
            </div>
            
            <div class="stat-card mobile-users">
              <div class="stat-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                  <line x1="12" y1="18" x2="12" y2="18"></line>
                </svg>
              </div>
              <div class="stat-info">
                <h3>Mobile Nutzer</h3>
                <p class="stat-value">{{ summaryData.mobile_count || 0 }}</p>
              </div>
            </div>
            
            <div class="stat-card desktop-users">
              <div class="stat-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </div>
              <div class="stat-info">
                <h3>Desktop Nutzer</h3>
                <p class="stat-value">{{ summaryData.desktop_count || 0 }}</p>
              </div>
            </div>
          </div>
          
          <div class="top-pages-section">
            <h3>Meistbesuchte Seiten</h3>
            <div class="top-pages-table-container">
              <table class="top-pages-table">
                <thead>
                  <tr>
                    <th>Seite</th>
                    <th>Aufrufe</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="page in summaryData.top_pages" :key="page.page">
                    <td>{{ page.page }}</td>
                    <td class="text-right">{{ page.count }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Zeitverlauf-Tab -->
      <div v-if="activeTab === 'daily'" class="daily-tab">
        <div class="filter-controls">
          <label for="timeRange">Zeitraum:</label>
          <select id="timeRange" v-model="timeRange">
            <option value="7">7 Tage</option>
            <option value="30">30 Tage</option>
            <option value="90">90 Tage</option>
            <option value="365">365 Tage</option>
          </select>
        </div>
        
        <div class="chart-container chart-daily">
          <h3>Besucherentwicklung im Zeitverlauf</h3>
          <Line 
            v-if="dailyChartData" 
            :data="dailyChartData" 
            :options="chartOptions"
            :key="`daily-chart-${forceRender}`"
          />
        </div>
      </div>
      
      <!-- Browser-Tab -->
      <div v-if="activeTab === 'browsers'" class="browsers-tab">
        <div class="chart-container chart-browsers">
          <h3>Browser-Verteilung</h3>
          <Doughnut 
            v-if="browserChartData" 
            :data="browserChartData" 
            :options="chartOptions"
            :key="`browser-chart-${forceRender}`"
          />
        </div>
        
        <div v-if="browserData" class="data-table data-browsers">
          <h3>Detaillierte Browser-Statistik</h3>
          <table>
            <thead>
              <tr>
                <th>Browser</th>
                <th>Besucher</th>
                <th>Anteil</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="browser in browserData" :key="browser.browser">
                <td>{{ browser.browser }}</td>
                <td>{{ browser.count }}</td>
                <td>{{ ((browser.count / browserData.reduce((sum, b) => sum + parseInt(b.count), 0)) * 100).toFixed(1) }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Betriebssysteme-Tab -->
      <div v-if="activeTab === 'os'" class="os-tab">
        <div class="chart-container chart-os">
          <h3>Betriebssystem-Verteilung</h3>
          <Pie 
            v-if="osChartData" 
            :data="osChartData" 
            :options="chartOptions"
            :key="`os-chart-${forceRender}`"
          />
        </div>
        
        <div v-if="osData" class="data-table data-os">
          <h3>Detaillierte Betriebssystem-Statistik</h3>
          <table>
            <thead>
              <tr>
                <th>Betriebssystem</th>
                <th>Besucher</th>
                <th>Anteil</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="os in osData" :key="os.os">
                <td>{{ os.os }}</td>
                <td>{{ os.count }}</td>
                <td>{{ ((os.count / osData.reduce((sum, o) => sum + parseInt(o.count), 0)) * 100).toFixed(1) }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Geräte-Tab -->
      <div v-if="activeTab === 'devices'" class="devices-tab">
        <div class="chart-container chart-devices">
          <h3>Gerätetyp-Verteilung</h3>
          <Doughnut 
            v-if="deviceChartData" 
            :data="deviceChartData" 
            :options="chartOptions"
            :key="`device-chart-${forceRender}`"
          />
        </div>
        
        <div v-if="deviceData" class="data-table data-devices">
          <h3>Detaillierte Gerätetyp-Statistik</h3>
          <table>
            <thead>
              <tr>
                <th>Gerätetyp</th>
                <th>Besucher</th>
                <th>Anteil</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="device in deviceData" :key="device.device_type">
                <td>{{ device.device_type }}</td>
                <td>{{ device.count }}</td>
                <td>{{ ((device.count / deviceData.reduce((sum, d) => sum + parseInt(d.count), 0)) * 100).toFixed(1) }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Seiten-Tab -->
      <div v-if="activeTab === 'pages' && pageData" class="pages-tab">
        <div class="data-table data-pages">
          <h3>Statistik der besuchten Seiten</h3>
          <table>
            <thead>
              <tr>
                <th>Seite</th>
                <th>Aufrufe</th>
                <th>Anteil</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="page in pageData" :key="page.page">
                <td>{{ page.page }}</td>
                <td>{{ page.count }}</td>
                <td>{{ ((page.count / pageData.reduce((sum, p) => sum + parseInt(p.count), 0)) * 100).toFixed(1) }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Länder-Tab -->
      <div v-if="activeTab === 'countries'" class="countries-tab">
        <div class="chart-container chart-countries">
          <h3>Verteilung nach Ländern</h3>
          <Pie 
            v-if="countryChartData" 
            :data="countryChartData" 
            :options="chartOptions"
            :key="`country-chart-${forceRender}`"
          />
        </div>
        
        <div v-if="countryData" class="data-table data-countries">
          <h3>Detaillierte Länder-Statistik</h3>
          <table>
            <thead>
              <tr>
                <th>Land</th>
                <th>Besucher</th>
                <th>Anteil</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="country in countryData.slice(0, 20)" :key="country.country">
                <td>{{ country.country }}</td>
                <td>{{ country.count }}</td>
                <td>{{ ((country.count / countryData.reduce((sum, c) => sum + parseInt(c.count), 0)) * 100).toFixed(1) }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.stats-manager {
  padding: 1.5rem;
}

.section-header {
  margin-bottom: 2rem;
}

.section-header h2 {
  font-size: 1.75rem;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
}

.section-description {
  color: var(--text-muted);
}

.admin-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 1rem;
}

.tab-button {
  background: none;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-weight: 500;
  color: var(--text-muted);
  transition: all 0.2s ease;
}

.tab-button:hover {
  color: var(--primary-color);
  background-color: rgba(0, 0, 0, 0.03);
}

body.dark-mode .tab-button:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.tab-button.active {
  background-color: var(--primary-color);
  color: white;
}

.stats-content {
  margin-bottom: 2rem;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

body.dark-mode .spinner {
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-left-color: var(--primary-color);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  background-color: #ffebee;
  color: #e53935;
  padding: 1.5rem;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  gap: 1rem;
}

body.dark-mode .error-message {
  background-color: rgba(229, 57, 53, 0.2);
}

/* Übersichts-Tab */
.card-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.stats-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

/* Statistik-Karten Styling */
.stats-card {
  background-color: var(--white);
  padding: 1.5rem;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  border-left: 4px solid var(--primary-color);
}

.stats-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-md);
}

.stats-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.05;
  z-index: 0;
  border-radius: var(--radius-md);
  transition: all 0.3s ease;
}

.stats-card:hover::before {
  opacity: 0.1;
}

.today-card {
  border-left-color: #4CAF50;
}

.today-card::before {
  background: linear-gradient(135deg, #4CAF50 0%, #81C784 100%);
}

.yesterday-card {
  border-left-color: #2196F3;
}

.yesterday-card::before {
  background: linear-gradient(135deg, #2196F3 0%, #64B5F6 100%);
}

.week-card {
  border-left-color: #9C27B0;
}

.week-card::before {
  background: linear-gradient(135deg, #9C27B0 0%, #CE93D8 100%);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
}

.card-icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: rgba(0, 132, 61, 0.1);
  color: var(--primary-color);
}

.today-card .card-icon {
  background-color: rgba(76, 175, 80, 0.1);
  color: #4CAF50;
}

.yesterday-card .card-icon {
  background-color: rgba(33, 150, 243, 0.1);
  color: #2196F3;
}

.week-card .card-icon {
  background-color: rgba(156, 39, 176, 0.1);
  color: #9C27B0;
}

.card-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-color);
}

.card-body {
  position: relative;
  z-index: 1;
}

.stat-value {
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--text-color);
  margin-bottom: 0.25rem;
}

.today-card .stat-value {
  color: #2E7D32;
}

.yesterday-card .stat-value {
  color: #1565C0;
}

.week-card .stat-value {
  color: #6A1B9A;
}

body.dark-mode .today-card .stat-value {
  color: #A5D6A7;
}

body.dark-mode .yesterday-card .stat-value {
  color: #90CAF9;
}

body.dark-mode .week-card .stat-value {
  color: #E1BEE7;
}

.stat-label {
  font-size: 0.9rem;
  color: var(--text-muted);
}

/* Dunkelmodus-Anpassungen */
body.dark-mode .stats-card {
  background-color: rgba(255, 255, 255, 0.05);
}

body.dark-mode .card-icon {
  background-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode .today-card .card-icon {
  background-color: rgba(76, 175, 80, 0.2);
}

body.dark-mode .yesterday-card .card-icon {
  background-color: rgba(33, 150, 243, 0.2);
}

body.dark-mode .week-card .card-icon {
  background-color: rgba(156, 39, 176, 0.2);
}

.reset-button {
  background: none;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-weight: 500;
  color: var(--text-muted);
  transition: all 0.2s ease;
}

.reset-button:hover {
  color: var(--primary-color);
  background-color: rgba(0, 0, 0, 0.03);
}

body.dark-mode .reset-button:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.reset-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.reset-success,
.reset-error {
  padding: 0.5rem 1rem;
  border-radius: var(--radius-sm);
  background-color: var(--white);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  animation: fadeIn 0.3s ease-in-out;
}

.reset-success {
  color: #2e7d32;
  border-left: 3px solid #2e7d32;
}

.reset-error {
  color: #d32f2f;
  border-left: 3px solid #d32f2f;
}

body.dark-mode .reset-success {
  color: #81c784;
  background-color: rgba(0, 0, 0, 0.2);
}

body.dark-mode .reset-error {
  color: #e57373;
  background-color: rgba(0, 0, 0, 0.2);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background-color: var(--white);
  padding: 1.5rem;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
  gap: 1.25rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  border-left: 4px solid var(--primary-color);
  z-index: 1;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.05;
  z-index: 0;
  border-radius: var(--radius-md);
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-md);
}

.stat-card:hover::before {
  opacity: 0.1;
}

.total-visits {
  border-left-color: #FF9800;
}

.total-visits::before {
  background: linear-gradient(135deg, #FF9800 0%, #FFB74D 100%);
}

.unique-visitors {
  border-left-color: #00BCD4;
}

.unique-visitors::before {
  background: linear-gradient(135deg, #00BCD4 0%, #4DD0E1 100%);
}

.mobile-users {
  border-left-color: #F44336;
}

.mobile-users::before {
  background: linear-gradient(135deg, #F44336 0%, #EF9A9A 100%);
}

.desktop-users {
  border-left-color: #3F51B5;
}

.desktop-users::before {
  background: linear-gradient(135deg, #3F51B5 0%, #7986CB 100%);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  position: relative;
  z-index: 1;
}

.total-visits .stat-icon {
  background-color: rgba(255, 152, 0, 0.1);
  color: #FF9800;
}

.unique-visitors .stat-icon {
  background-color: rgba(0, 188, 212, 0.1);
  color: #00BCD4;
}

.mobile-users .stat-icon {
  background-color: rgba(244, 67, 54, 0.1);
  color: #F44336;
}

.desktop-users .stat-icon {
  background-color: rgba(63, 81, 181, 0.1);
  color: #3F51B5;
}

body.dark-mode .stat-icon {
  background-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode .total-visits .stat-icon {
  background-color: rgba(255, 152, 0, 0.2);
}

body.dark-mode .unique-visitors .stat-icon {
  background-color: rgba(0, 188, 212, 0.2);
}

body.dark-mode .mobile-users .stat-icon {
  background-color: rgba(244, 67, 54, 0.2);
}

body.dark-mode .desktop-users .stat-icon {
  background-color: rgba(63, 81, 181, 0.2);
}

.stat-info {
  position: relative;
  z-index: 1;
}

.stat-info h3 {
  font-size: 0.9rem;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.stat-card .stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0;
}

.total-visits .stat-value {
  color: #E65100;
}

.unique-visitors .stat-value {
  color: #006064;
}

.mobile-users .stat-value {
  color: #B71C1C;
}

.desktop-users .stat-value {
  color: #1A237E;
}

body.dark-mode .total-visits .stat-value {
  color: #FFD180;
}

body.dark-mode .unique-visitors .stat-value {
  color: #80DEEA;
}

body.dark-mode .mobile-users .stat-value {
  color: #FFCDD2;
}

body.dark-mode .desktop-users .stat-value {
  color: #9FA8DA;
}

body.dark-mode .stat-card {
  background-color: rgba(255, 255, 255, 0.05);
}

.top-pages-section {
  background-color: var(--white);
  padding: 1.5rem;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border-top: 4px solid #607D8B;
}

.top-pages-section:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-md);
}

body.dark-mode .top-pages-section {
  background-color: rgba(255, 255, 255, 0.05);
}

.top-pages-section h3 {
  font-size: 1.1rem;
  color: #455A64;
  margin-bottom: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.top-pages-section h3::before {
  content: '📊';
  font-size: 1.2rem;
}

body.dark-mode .top-pages-section h3 {
  color: #B0BEC5;
}

.top-pages-table-container {
  max-height: 300px;
  overflow-y: auto;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-color);
}

.top-pages-table {
  width: 100%;
  border-collapse: collapse;
}

.top-pages-table th,
.top-pages-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-color);
}

.top-pages-table th {
  text-align: left;
  color: var(--text-muted);
  font-weight: 500;
  font-size: 0.9rem;
  background-color: rgba(96, 125, 139, 0.05);
}

.top-pages-table tbody tr {
  transition: background-color 0.2s ease;
}

.top-pages-table tbody tr:hover {
  background-color: rgba(96, 125, 139, 0.07);
}

.top-pages-table tbody tr:last-child td {
  border-bottom: none;
}

.text-right {
  text-align: right;
}

/* Filter-Controls */
.filter-controls {
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: var(--white);
  padding: 1rem;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  border-left: 4px solid #3949AB;
}

.filter-controls label {
  font-weight: 500;
  color: var(--text-color);
}

.filter-controls select {
  padding: 0.5rem 1rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-color);
  background-color: var(--white);
  color: var(--text-color);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-controls select:hover {
  border-color: #3949AB;
}

.filter-controls select:focus {
  outline: none;
  border-color: #3949AB;
  box-shadow: 0 0 0 2px rgba(57, 73, 171, 0.2);
}

body.dark-mode .filter-controls {
  background-color: rgba(255, 255, 255, 0.05);
  border-left-color: #7986CB;
}

body.dark-mode .filter-controls select {
  background-color: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #E8EAF6;
}

body.dark-mode .filter-controls select:hover,
body.dark-mode .filter-controls select:focus {
  border-color: #7986CB;
}

/* Chart-Container */
.chart-container {
  background-color: var(--white);
  padding: 1.5rem;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  height: 350px;
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border-top: 4px solid var(--primary-color);
}

.chart-container:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-md);
}

.chart-container h3 {
  font-size: 1.1rem;
  margin-bottom: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.chart-daily {
  border-top-color: #3949AB;
}

.chart-daily h3 {
  color: #3949AB;
}

.chart-daily h3::before {
  content: '📈';
  font-size: 1.2rem;
}

.chart-browsers {
  border-top-color: #00BCD4;
}

.chart-browsers h3 {
  color: #00BCD4;
}

.chart-browsers h3::before {
  content: '🌐';
  font-size: 1.2rem;
}

.chart-os {
  border-top-color: #4CAF50;
}

.chart-os h3 {
  color: #4CAF50;
}

.chart-os h3::before {
  content: '💻';
  font-size: 1.2rem;
}

.chart-devices {
  border-top-color: #FF5722;
}

.chart-devices h3 {
  color: #FF5722;
}

.chart-devices h3::before {
  content: '��';
  font-size: 1.2rem;
}

.chart-countries {
  border-top-color: #673AB7;
}

.chart-countries h3 {
  color: #673AB7;
}

.chart-countries h3::before {
  content: '🌍';
  font-size: 1.2rem;
}

body.dark-mode .chart-container {
  background-color: rgba(255, 255, 255, 0.05);
}

body.dark-mode .chart-daily h3 {
  color: #7986CB;
}

body.dark-mode .chart-browsers h3 {
  color: #4DD0E1;
}

body.dark-mode .chart-os h3 {
  color: #81C784;
}

body.dark-mode .chart-devices h3 {
  color: #FF8A65;
}

body.dark-mode .chart-countries h3 {
  color: #B39DDB;
}

/* Data-Table */
.data-table {
  background-color: var(--white);
  padding: 1.5rem;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  overflow: auto;
  position: relative;
  transition: all 0.3s ease;
  border-top: 4px solid var(--primary-color);
}

.data-table:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-md);
}

.data-table h3 {
  font-size: 1.1rem;
  margin-bottom: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.data-table table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.data-table th,
.data-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-color);
}

.data-table th {
  text-align: left;
  color: var(--text-muted);
  font-weight: 500;
  font-size: 0.9rem;
}

.data-table tbody tr {
  transition: background-color 0.2s ease;
}

.data-table tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.data-browsers {
  border-top-color: #00BCD4;
}

.data-browsers h3 {
  color: #00BCD4;
}

.data-browsers h3::before {
  content: '🔍';
  font-size: 1.2rem;
}

.data-browsers th {
  background-color: rgba(0, 188, 212, 0.05);
}

.data-browsers tbody tr:hover {
  background-color: rgba(0, 188, 212, 0.05);
}

.data-os {
  border-top-color: #4CAF50;
}

.data-os h3 {
  color: #4CAF50;
}

.data-os h3::before {
  content: '🖥️';
  font-size: 1.2rem;
}

.data-os th {
  background-color: rgba(76, 175, 80, 0.05);
}

.data-os tbody tr:hover {
  background-color: rgba(76, 175, 80, 0.05);
}

.data-devices {
  border-top-color: #FF5722;
}

.data-devices h3 {
  color: #FF5722;
}

.data-devices h3::before {
  content: '📊';
  font-size: 1.2rem;
}

.data-devices th {
  background-color: rgba(255, 87, 34, 0.05);
}

.data-devices tbody tr:hover {
  background-color: rgba(255, 87, 34, 0.05);
}

.data-pages {
  border-top-color: #009688;
}

.data-pages h3 {
  color: #009688;
}

.data-pages h3::before {
  content: '📄';
  font-size: 1.2rem;
}

.data-pages th {
  background-color: rgba(0, 150, 136, 0.05);
}

.data-pages tbody tr:hover {
  background-color: rgba(0, 150, 136, 0.05);
}

.data-countries {
  border-top-color: #673AB7;
}

.data-countries h3 {
  color: #673AB7;
}

.data-countries h3::before {
  content: '🌎';
  font-size: 1.2rem;
}

.data-countries th {
  background-color: rgba(103, 58, 183, 0.05);
}

.data-countries tbody tr:hover {
  background-color: rgba(103, 58, 183, 0.05);
}

body.dark-mode .data-table {
  background-color: rgba(255, 255, 255, 0.05);
}

body.dark-mode .data-table tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode .data-browsers h3 {
  color: #4DD0E1;
}

body.dark-mode .data-browsers th {
  background-color: rgba(77, 208, 225, 0.1);
}

body.dark-mode .data-browsers tbody tr:hover {
  background-color: rgba(77, 208, 225, 0.1);
}

body.dark-mode .data-os h3 {
  color: #81C784;
}

body.dark-mode .data-os th {
  background-color: rgba(129, 199, 132, 0.1);
}

body.dark-mode .data-os tbody tr:hover {
  background-color: rgba(129, 199, 132, 0.1);
}

body.dark-mode .data-devices h3 {
  color: #FF8A65;
}

body.dark-mode .data-devices th {
  background-color: rgba(255, 138, 101, 0.1);
}

body.dark-mode .data-devices tbody tr:hover {
  background-color: rgba(255, 138, 101, 0.1);
}

body.dark-mode .data-pages h3 {
  color: #4DB6AC;
}

body.dark-mode .data-pages th {
  background-color: rgba(77, 182, 172, 0.1);
}

body.dark-mode .data-pages tbody tr:hover {
  background-color: rgba(77, 182, 172, 0.1);
}

body.dark-mode .data-countries h3 {
  color: #B39DDB;
}

body.dark-mode .data-countries th {
  background-color: rgba(179, 157, 219, 0.1);
}

body.dark-mode .data-countries tbody tr:hover {
  background-color: rgba(179, 157, 219, 0.1);
}

@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
  
  .admin-tabs {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 0.5rem;
  }
  
  .tab-button {
    white-space: nowrap;
  }
}
</style> 